/**
 * HTTP middleware functions (interceptors)
 * Different middleware use different patterns based on their requirements:
 * - Simple interceptors: Return handler directly
 * - Stateful interceptors: Accept dependencies and return handler
 *
 * @typedef {import('axios').AxiosError} AxiosError
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 * @typedef {import('axios').AxiosInstance} AxiosInstance
 * @typedef {import('axios').InternalAxiosRequestConfig} InternalAxiosRequestConfig
 */

import { logHttpErrorToSentry, logError } from '../utils/sentry-logger';
import { transformNetlifyTimeout, refreshTokenAndRetry } from './utils';

// ============================================================================
// Base HTTP Client Middleware
// ============================================================================

/**
 * Response error interceptor for base HTTP client
 * Handles Netlify timeout detection and error transformation
 * @param {AxiosError} error - The axios error object
 * @returns {Promise<never>} Rejected promise with transformed error
 */
export const handleBaseErrors = (error) => {
    // Transform Netlify timeouts into proper Errors
    const transformedError = transformNetlifyTimeout(error);
    // Log to Sentry
    logHttpErrorToSentry(transformedError);
    // Reject with transformed error
    return Promise.reject(transformedError);
};

// ============================================================================
// JWT Authentication Middleware
// ============================================================================

/**
 * Request interceptor that injects JWT into all requests
 * @param {object} deps - Required dependencies
 * @param {import('../utils/JWTokenHandler').default} deps.tokenHandler - JWT token handler instance
 * @param {() => boolean} deps.isTokenHandlerInitialized - Function that returns whether JWT handler is initialized
 * @param {(value: boolean) => void} deps.setTokenHandlerInitialized - Function to mark JWT handler as initialized
 * @returns {(config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>} Axios request interceptor
 */
export const injectJWT = ({
    tokenHandler,
    isTokenHandlerInitialized,
    setTokenHandlerInitialized,
}) => {
    return async (config) => {
        try {
            // Initialize JWT if needed
            if (!isTokenHandlerInitialized()) {
                const result = await tokenHandler.init();
                if (result?.error) {
                    // Let the request proceed without JWT and fail properly
                    return config;
                }
                setTokenHandlerInitialized(true);
                tokenHandler.maintainSession();
            }

            // Get the JWT after ensuring initialization
            const jwt = tokenHandler.jwt;

            if (jwt) {
                // Parse the data if it's already stringified by axios
                let originalData = config.data;
                if (typeof originalData === 'string') {
                    try {
                        originalData = JSON.parse(originalData);
                    } catch (e) {
                        // Not JSON, treat as plain string
                    }
                }
                originalData = originalData || {};

                // Only add JWT if not already present (avoid double-wrapping on retries)
                if (!originalData.jwt) {
                    config.data = {
                        jwt,
                        data: originalData.data ?? originalData,
                        options: originalData.options ?? {},
                    };
                }
            }
            // If no JWT available, let request proceed without auth
            // Backend will return 401/403, triggering handleAuthErrors for retry
        } catch (error) {
            // Unexpected error during JWT injection - this shouldn't happen
            // Log as exception since this is NOT logged by tokenHandler
            logError(error, {
                contexts: {
                    http: {
                        url: config.url,
                        method: config.method,
                    },
                },
                tags: {
                    component: 'jwt-middleware',
                },
            });
            // Don't block the request, let it fail with proper error
        }

        return config;
    };
};

/**
 * Response interceptor that handles authentication errors and retries with refreshed JWT
 * @param {object} deps - Required dependencies
 * @param {import('../utils/JWTokenHandler').default} deps.tokenHandler - JWT token handler instance
 * @param {AxiosInstance} deps.httpClient - HTTP client instance for retry
 * @returns {(error: AxiosError) => Promise<AxiosResponse | never>} Axios response error interceptor
 */
export const handleAuthErrors = ({ tokenHandler, httpClient }) => {
    return async (error) => {
        const originalRequest = error.config;

        // Prevent infinite retry loops
        if (originalRequest._retry) {
            throw error;
        }

        // Guard clause for network errors (no response)
        if (!error.response) {
            throw error;
        }

        // Check for token expiration (match various phrasings)
        const errorMessage = error.response.data?.error?.message || '';
        const isTokenExpired = /token.*expired|expired.*token|jwt.*expired|expired.*jwt/i.test(
            errorMessage,
        );

        // Check for auth errors (401/403)
        const isAuthError = error.response.status === 401 || error.response.status === 403;

        // Only retry for auth-related errors, not for other failures (404, 500, etc.)
        if (!isTokenExpired && !isAuthError) {
            throw error;
        }

        originalRequest._retry = true;

        try {
            return await refreshTokenAndRetry({ tokenHandler, originalRequest, httpClient });
        } catch (refreshError) {
            // Token refresh failed, return original auth error
            // Error already logged by tokenHandler
            throw error;
        }
    };
};
